import * as React from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { PortalWrapper } from 'modules/core/components/PortalWrapper'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import type { RouteContext } from 'modules/employees/lib/types'
import type Employee from 'modules/employees/store/models/Employee'
import Leaves from 'modules/timeoff/components/Leaves'
import { type AllowanceIncidences } from 'modules/timeoff/store/collections/allowanceIncidences'
import { type Allowances } from 'modules/timeoff/store/collections/allowances'
import { type TimeoffPolicies } from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import { type PolicyTimelines } from 'modules/timeoff/store/collections/timeoffPolicies/policyTimelines'

interface Props {
  employee: Employee
  canManageTimeoff: boolean
  canCreateLeaves: boolean
  canApproveLeaves: boolean
  hasManualAdjustmentOptions: boolean
  allowances: Allowances
  allowanceIncidences: AllowanceIncidences
  policies: TimeoffPolicies
  policyTimelines: PolicyTimelines
  loading: boolean
}

// Memoize TimeOffCard to avoid unnecessary re-renders when props do not change
const TimeOffCard = React.memo(
  ({
    employee,
    canManageTimeoff,
    canCreateLeaves,
    canApproveLeaves,
    hasManualAdjustmentOptions,
    allowances,
    allowanceIncidences,
    policies,
    policyTimelines,
    loading,
    children,
  }: React.PropsWithChildren<Props>) => {
    const { navigation } = useNavigation()
    const context = useOutletContext<RouteContext>()
    const { cardEmployeeRight } = context ?? {}
    const navigate = useNavigate()

    const settingsOnClick = React.useCallback(() => {
      const timeOffPolicyId = policyTimelines.timeline.current.timeoff_policy_id
      navigate(navigation.settings.timeoffPolicies.id.details.fullPath({ id: timeOffPolicyId }))
    }, [policyTimelines.timeline, navigation.settings.timeoffPolicies.id.details, navigate])

    const renderAction = React.useCallback(() => {
      if (!canManageTimeoff) return null
      return <Button onClick={settingsOnClick} type="primary" iconName="settings" />
    }, [canManageTimeoff, settingsOnClick])

    const renderContent = React.useCallback(() => {
      // Show loading state while secondary data (allowanceIncidences) is being fetched
      if (loading) return null

      return (
        <Leaves
          employee={employee}
          hasManualAdjustmentOptions={hasManualAdjustmentOptions}
          canCreateLeaves={canCreateLeaves}
          canApproveLeaves={canApproveLeaves}
          policyTimelines={policyTimelines}
          allowances={allowances}
          allowanceIncidences={allowanceIncidences}
          policies={policies}
        />
      )
    }, [
      loading,
      employee,
      hasManualAdjustmentOptions,
      canCreateLeaves,
      canApproveLeaves,
      policyTimelines,
      allowances,
      allowanceIncidences,
      policies,
    ])

    return (
      <Box width="full" flexGrow>
        {cardEmployeeRight && (
          <PortalWrapper container={cardEmployeeRight}>{renderAction()}</PortalWrapper>
        )}
        {children}
        {renderContent()}
      </Box>
    )
  }
)

// Set displayName for better debugging in React DevTools and error messages
TimeOffCard.displayName = 'TimeOffCard'

export default TimeOffCard
